import moment from 'moment';
import {FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';
import { Checkbox, Table, TablePagination, TableSort } from 'thermia-react-container';
import DevicesIcon from '../../../components/assets/icons/devicesIcon';
import TableSkeleton from '../../../components/table/skeleton';
import { useDeviceFilter } from '../../../hooks/useDeviceFilter';
import { Device } from '../../../types/device';
import {styled} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {useRegistrationService} from "../../../service/registrationService";
import {RegistrationsContext} from "../../../context/contextProvider";

interface UnregisteredDeviceForTable {
  selected: string;
  id: string;
  icon: ReactNode;
  serialNumber: string;
  articleName: string;
  articleNumber: string;
  deliverDate: string;
  orderNumber: string;
  registrationId: string
}

const StyledStatusWrapper = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  flexDirection: 'column'
}));

const StyledStatusText = styled('span')(() => ({
  textDecoration: 'underline'
}));

interface Props {
  devices: Device[];
  selectedDevices: string[];
  setSelectedDevices: (deviceIds: string[]) => void;
  isLoading: boolean;
}

const UnregisteredDevicesTable: FC<Props> = ({ devices, selectedDevices, setSelectedDevices, isLoading }) => {
  const { t } = useTranslation('registrations');
  const deviceFilter = useDeviceFilter();
  const navigate = useNavigate();
  const { getRegistration } = useRegistrationService();
  const { registrations } = useContext(RegistrationsContext);
  const { setActiveRegistration } = registrations;

  const handleSelectDevice = useCallback(
    (id: string) => {
      if (selectedDevices.includes(id)) {
        return setSelectedDevices(selectedDevices.filter((deviceId) => deviceId !== id));
      }
      setSelectedDevices(selectedDevices.concat(id));
    },
    [selectedDevices, setSelectedDevices]
  );

  const selectIconWithStatus = useCallback((device: UnregisteredDeviceForTable) => {
    if(device.registrationId !== '') {
      return (
        <StyledStatusText onClick={() => handelEditRegistration(device)}>
          {t('registrations.general.ongoingRegistration')}
        </StyledStatusText>
      );
    }
    return (<></>);
  }, [t]);

  const columns: Column<UnregisteredDeviceForTable>[] = useMemo(() => {
    return [
      {
        accessor: 'selected',
        Header: t('registrations.general.select'),
        Cell: (props) => (
          <>
            {props.row.original.registrationId === '' && (
              <Checkbox
                label={''}
                checked={selectedDevices.includes(props.row.original.serialNumber)}
                onClick={() => handleSelectDevice(props.row.original.serialNumber)}
              />
            )}
          </>
        )
      },
      {
        accessor: 'icon',
        Header: ''
      },
      {
        accessor: 'serialNumber',
        sortable: true,
        Header: t('registrations.general.serialNumber') ?? ''
      },
      {
        accessor: 'articleName',
        sortable: true,
        Header: t('registrations.general.articleName') ?? ''
      },
      {
        accessor: 'articleNumber',
        sortable: true,
        Header: t('registrations.general.articleNumber') ?? ''
      },
      {
        accessor: 'deliverDate',
        sortable: true,
        Header: t('registrations.general.deliverDate') ?? ''
      },
      {
        accessor: 'orderNumber',
        sortable: true,
        Header: t('registrations.general.orderNumber') ?? ''
      },
      {
        accessor: 'registrationId',
        sortable: false,
        Header: t('registrations.general.status') ?? '',
        Cell: (prop) => (
        <>
          <StyledStatusWrapper>
              <span>
                {selectIconWithStatus(prop.row.original)}
              </span>
          </StyledStatusWrapper>
        </>
        )
      }
    ];
  }, [handleSelectDevice, selectedDevices, t]);

  const fetchDataFunction = (
    pagination: TablePagination | null,
    search: string | null,
    sort: TableSort | null
  ): Promise<{ data: UnregisteredDeviceForTable[]; totalCount: number }> => {
    return new Promise((resolve) => {
      const data = flattenedDevices(search);
      let filterTableBySearch = filterBySearch(search?.trim() ?? '', data);
      if (sort) {
        filterTableBySearch = filterTableBySearch.sort((a, b) => {
          const key = sort.column as keyof Omit<UnregisteredDeviceForTable, 'icon'>;
          return (a[key] > b[key] ? 1 : -1) * (sort.direction === 'asc' ? 1 : -1);
        });
      }
      const filteredData = filterTableBySearch.slice(pagination!.skip, pagination!.skip + pagination!.take);
      const totalCount = filterTableBySearch.length;
      resolve({ data: filteredData, totalCount });
    });
  };

  const [tableMethods] = useState({
    refreshData: () => Promise.resolve() // function will be set by the table
  });

  const refreshTableData = useCallback(async () => {
    await tableMethods.refreshData();
  }, [tableMethods]);

  useEffect(() => {
    refreshTableData();
  }, [devices, refreshTableData]);

  const filterBySearch = (search: string, data: UnregisteredDeviceForTable[]) => {
    return data.filter((row) => {
      return (
        row.serialNumber.toLowerCase().includes(search.toLowerCase()) ||
        row.articleName.toLowerCase().includes(search.toLowerCase()) ||
        row.articleNumber.toLowerCase().includes(search.toLowerCase()) ||
        (row.deliverDate ?? '').toLowerCase().includes(search.toLowerCase()) ||
        row.orderNumber.toLowerCase().includes(search.toLowerCase())
      );
    });
  };

  const flattenedDevices = useCallback(
    (searchString: string | null) : UnregisteredDeviceForTable[] => {
      return devices
        .filter((device) => deviceFilter(device, searchString ?? undefined))
        .map((device) => ({
          selected: '',
          id: device.id,
          serialNumber: device.serialNumber,
          articleName: device.articleName,
          articleNumber: device.articleNumber,
          deliverDate: device.deliverDate ? moment(device.deliverDate).format('YYYY-MM-DD') : '',
          orderNumber: device.orderNumber ?? '',
          icon: <DevicesIcon />,
          registrationId: device.registrationId ?? '',
        }));
    },
    [deviceFilter, devices]
  );

  const handelEditRegistration = async (device: UnregisteredDeviceForTable) => {
    const registration = await getRegistration(device.registrationId);
    if(!registration) {
      return;
    }
    setActiveRegistration(registration);
    navigate(`/registrations/${device.registrationId}/customer`, { state: { registrationIsCompleted: false } })
  }

  return (
    <>
      {devices.length === 0 && isLoading ? (
        <TableSkeleton />
      ) : (
        <Table exposedMethods={tableMethods} columns={columns} fetchData={fetchDataFunction} sortable={true} searchable pagination />
      )}
    </>
  );
};

export default UnregisteredDevicesTable;
